import { motion, useReducedMotion } from 'framer-motion'
import { Footer } from '@/components/Footer'
import { Navbar } from '@/components/Navbar'

const ease: [number, number, number, number] = [0.16, 1, 0.3, 1]

export function PrivacyPolicyPage() {
    const reduced = useReducedMotion()
    const m = (d: number) => reduced ? undefined : { duration: 0.7, delay: d, ease }

    return (
        <div className="min-h-screen bg-[#211d18] text-[#eee8dc]">
            <Navbar variant="dark" />

            <main className="relative mx-auto max-w-[820px] px-6 pb-24 pt-32 sm:px-8 sm:pt-40">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={m(0)}
                >
                    <span className="font-body text-[11px] uppercase tracking-[0.2em] text-[#c8a88a]">
                        Legal
                    </span>
                    <h1 className="mt-4 font-serif text-3xl sm:text-4xl md:text-5xl text-[#eee8dc]">
                        Privacy Policy
                    </h1>
                    <p className="mt-3 font-body text-sm text-[#eee8dc]/40">
                        Last updated: February 2025
                    </p>

                    <div className="mt-14 space-y-10">
                        <Section title="1. Introduction">
                            <p>
                                Welcome to Jpdz ("we," "our," or "us"), a product of Jpdz Labs. This Privacy Policy
                                explains how we collect, use, disclose, and safeguard your information when you use
                                our AI-powered developer tools and services.
                            </p>
                        </Section>

                        <Section title="2. Information We Collect">
                            <p><strong className="text-[#eee8dc]/90">Information you provide:</strong></p>
                            <ul>
                                <li>Account information (email, username) when you sign up</li>
                                <li>Device information you choose to share for personalized recommendations</li>
                                <li>Chat conversations with our AI assistant</li>
                                <li>Feedback and support communications</li>
                            </ul>

                            <p><strong className="text-[#eee8dc]/90">Information collected automatically:</strong></p>
                            <ul>
                                <li>Usage data and analytics</li>
                                <li>Device type and browser information</li>
                                <li>IP address and approximate location</li>
                            </ul>
                        </Section>

                        <Section title="3. How We Use Your Information">
                            <ul>
                                <li>Provide and improve our AI-powered developer tools</li>
                                <li>Personalize your experience based on your workflow</li>
                                <li>Communicate with you about updates and features</li>
                                <li>Analyze usage patterns to improve our service</li>
                                <li>Ensure security and prevent fraud</li>
                            </ul>
                        </Section>

                        <Section title="4. Information Sharing">
                            <p>
                                We do not sell your personal information. We may share information with:
                            </p>
                            <ul>
                                <li>Service providers who help us operate (hosting, analytics)</li>
                                <li>AI model providers for generating recommendations (anonymized)</li>
                                <li>Law enforcement when required by law</li>
                            </ul>
                        </Section>

                        <Section title="5. Google OAuth">
                            <p>
                                When you sign in with Google, we receive your email address and basic profile
                                information. We use this solely for authentication and do not access your
                                Google Drive, Gmail, or other Google services.
                            </p>
                        </Section>

                        <Section title="6. Data Security">
                            <p>
                                We implement industry-standard security measures including encryption, secure
                                servers, and regular security audits. However, no method of transmission over
                                the Internet is 100% secure.
                            </p>
                        </Section>

                        <Section title="7. Your Rights">
                            <p>You have the right to:</p>
                            <ul>
                                <li>Access your personal data</li>
                                <li>Correct inaccurate data</li>
                                <li>Delete your account and data</li>
                                <li>Export your data</li>
                                <li>Opt out of marketing communications</li>
                            </ul>
                        </Section>

                        <Section title="8. Cookies">
                            <p>
                                We use essential cookies for authentication and functionality. We also use
                                analytics cookies to understand how you use our service. You can control
                                cookie preferences in your browser settings.
                            </p>
                        </Section>

                        <Section title="9. Children's Privacy">
                            <p>
                                Our service is not intended for users under 13 years of age. We do not
                                knowingly collect information from children under 13.
                            </p>
                        </Section>

                        <Section title="10. Changes to This Policy">
                            <p>
                                We may update this Privacy Policy from time to time. We will notify you of
                                any changes by posting the new policy on this page and updating the "Last
                                updated" date.
                            </p>
                        </Section>

                        <Section title="11. Contact Us">
                            <p>
                                If you have questions about this Privacy Policy, please contact us at:
                            </p>
                            <p className="text-[#c8a88a]">
                                ratnam@jpdz.app
                            </p>
                        </Section>
                    </div>
                </motion.div>
            </main>

            <Footer />
        </div>
    )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <section className="border-t border-[#eee8dc]/[0.06] pt-8">
            <h2 className="font-serif text-lg sm:text-xl text-[#eee8dc]/90 mb-4">{title}</h2>
            <div className="font-body text-[15px] leading-[1.8] text-[#eee8dc]/55 space-y-4 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-2 [&_li]:text-[#eee8dc]/50">
                {children}
            </div>
        </section>
    )
}
